import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface ModelFormData {
  legalName: string;
  stageNames: string[];
  dateOfBirth: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  phone: string;
  email: string;
}

export default function ModelNew() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [stageNamesInput, setStageNamesInput] = useState('');

  const [formData, setFormData] = useState<ModelFormData>({
    legalName: '',
    stageNames: [],
    dateOfBirth: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
    },
    phone: '',
    email: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: ModelFormData) => api.post<{ model: { id: string } }>('/models', data),
    onSuccess: (response) => {
      navigate(`/models/${response.data.model.id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create model');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Parse stage names from comma-separated input
    const stageNames = stageNamesInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    createMutation.mutate({
      ...formData,
      stageNames,
    });
  };

  const updateAddress = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Add New Model</h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>Personal Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="legalName">Legal Name *</label>
                <input
                  type="text"
                  id="legalName"
                  value={formData.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                  required
                  placeholder="Full legal name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth *</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="stageNames">Stage Names</label>
              <input
                type="text"
                id="stageNames"
                value={stageNamesInput}
                onChange={(e) => setStageNamesInput(e.target.value)}
                placeholder="Comma-separated stage names"
              />
              <small>Enter multiple names separated by commas</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="model@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Address</h3>

            <div className="form-group">
              <label htmlFor="line1">Street Address *</label>
              <input
                type="text"
                id="line1"
                value={formData.address.line1}
                onChange={(e) => updateAddress('line1', e.target.value)}
                required
                placeholder="123 Main Street"
              />
            </div>

            <div className="form-group">
              <label htmlFor="line2">Apartment, Suite, etc.</label>
              <input
                type="text"
                id="line2"
                value={formData.address.line2}
                onChange={(e) => updateAddress('line2', e.target.value)}
                placeholder="Apt 4B"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  required
                  placeholder="Los Angeles"
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State *</label>
                <input
                  type="text"
                  id="state"
                  value={formData.address.state}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  required
                  placeholder="CA"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">ZIP Code *</label>
                <input
                  type="text"
                  id="postalCode"
                  value={formData.address.postalCode}
                  onChange={(e) => updateAddress('postalCode', e.target.value)}
                  required
                  placeholder="90001"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                value={formData.address.country}
                onChange={(e) => updateAddress('country', e.target.value)}
                placeholder="United States"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/models')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
